import React, { useEffect, useState } from 'react';
import { ClientApiProps, parseQuery } from '../common';
import { Link, LinkProps, Navigate, Route, Routes, useLocation, useMatch, useResolvedPath } from 'react-router-dom';
import { AssessorPage } from './Assessor';
import { OperatorPage } from './Operator';
import { ContactPage } from './ContactDetails';
import { Address, AddressSchema, AlertProvider, Spinner } from 'acma-spa';
import { IOperatorContact, IOperatorPayload, OperatorContact, OperatorRegistryContact, getApiClient } from '../api';
import { Client } from '../api';
import { AuthorisationModal } from '../common/AuthorisationModal';
import * as yup from 'yup';
import { CallsignTransferPage } from './CallsignTransfer';

// A Type-hack to get yup to accept Address fields.
type IModel = Omit<IOperatorContact, 'address1'> & { address1?: Address };

/**
 * Many of our forms require that the user have valid Contact Details.
 * Therefore, we do a simple validation here and ask the user to update their details is the validation fails.
 */
export const contactSchema: yup.ObjectSchema<IModel> = yup.object({
  title: yup.string().optional(),
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  otherName: yup.string().optional(),
  emailAddress: yup.string().email().required(),
  phone1: yup.string().required(),
  phone2: yup.string().optional(),
  address1: AddressSchema.default(undefined),
});

const Tab = ({ children, to, id }: LinkProps) => {
  const resolved = useResolvedPath(to);
  const match = useMatch({ path: resolved.pathname, end: true });

  return (
    <li className="nav-item">
      <Link to={to} id={id} className={`nav-link${match ? ' active' : ''}`} style={{ backgroundImage: 'none' }}>
        {children}
      </Link>
    </li>
  );
};

// Urls to subscreens
const assessorPath = '/amateurradio/assessor';
const operatorPath = '/amateurradio/operator';
const contactPath = '/amateurradio/contact';

const updateContactDetailsAlertMsgs = [<span key="msg">Please update your contact details before proceeding.</span>];

/** Message to display to users in the event of invalid contact details. */
const updateContactDetailsAlert = <AlertProvider color="primary" title="" noHeading messages={updateContactDetailsAlertMsgs} />;

const noDefaultPathAlertMsgs = [<span key="msg">This user is not an Assessor, not an Operator, and does not have any contact details.</span>];

const noDefaultPathAlert = <AlertProvider color="danger" title="No default route detected" messages={noDefaultPathAlertMsgs} />;

export interface LayoutProps extends ClientApiProps {}

/**
 * The Main component
 */
export const Layout: React.FC<LayoutProps> = (props) => {
  const [model, setModel] = useState<IOperatorPayload | null>();
  const [spinner, setSpinner] = useState('Loading...');
  const [authorisation, setAuthorisation] = useState(false);

  const [forceContactDetailsUpdate, setForceContactDetailsUpdate] = useState(false);

  const location = useLocation();
  const query = parseQuery(location.search);

  const load = async (client: Client) => {
    setSpinner('Loading...');

    try {
      const newModel = await client.operatorGet(query.invitationId);
      setModel(newModel);
    } finally {
      setSpinner('');
    }
  };

  useEffect(() => {
    const [client, abort] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
    load(client);
    return function () {
      abort.abort();
    };
  }, []);

  // When the model changes, validate the contact details. If invalid, ask the user to update them.
  // In practice, this will likely only happen on the first login.
  // We recheck on every change so that the page will revert back to a normal state once the Contact Details validate.
  useEffect(() => {
    const noContactDetails: boolean = !model?.contact;
    if (noContactDetails) {
      setForceContactDetailsUpdate(false);
      return;
    }

    const contactDetailsAreValid: boolean = contactSchema.isValidSync(model?.contact);
    setForceContactDetailsUpdate(!contactDetailsAreValid);
  }, [model]);

  const onChange = (contact: OperatorContact, registryContact?: OperatorRegistryContact) => {
    setModel({ ...model, contact, registryContact });
  };

  const isAssessor: boolean = model?.isAssessor === true;
  const isOperator: boolean = (model?.assessments?.length ?? 0) > 0 || (model?.callsigns?.length ?? 0) > 0;
  const hasContactDetails: boolean = !!model?.contact;

  // If the operator has missing contact details, force them to go to the Contact Details screen.
  if (forceContactDetailsUpdate && location.pathname !== contactPath) {
    return <Navigate to={contactPath} />;
  }

  let defaultPath: string | undefined;
  if (isAssessor) {
    defaultPath = assessorPath;
  } else if (isOperator) {
    defaultPath = operatorPath;
  } else if (hasContactDetails) {
    defaultPath = contactPath;
  }

  return (
    <>
      <h1>Amateur radio</h1>
      <Spinner isLoading={spinner ? true : false} loadingText={spinner}>
        {/* In the rare scenario of the user not having valid contact details, force them to update them here. */}
        {forceContactDetailsUpdate && updateContactDetailsAlert}

        <ul className="nav nav-tabs mb-3">
          {!forceContactDetailsUpdate && isAssessor && (
            <Tab to={assessorPath} id="assessor-tab">
              Assessor
            </Tab>
          )}
          {!forceContactDetailsUpdate && isOperator && (
            <Tab to={operatorPath} id="operator-tab">
              Operator
            </Tab>
          )}
          {hasContactDetails && (
            <Tab to={contactPath} id="contact-tab">
              Contact details
            </Tab>
          )}
        </ul>

        <Routes>
          <Route path="assessor" element={<AssessorPage {...props} documenation={model?.documenation ?? []} isAssessor={isAssessor} />} />
          <Route
            path="operator"
            element={
              <OperatorPage
                {...props}
                assessments={model?.assessments ?? []}
                callsigns={model?.callsigns ?? []}
                onChange={(m) => setModel({ ...model, ...m })}
                onSpinner={setSpinner}
                onAuthorisation={() => setAuthorisation(true)}
              />
            }
          />
          <Route
            path="contact"
            element={
              <ContactPage
                {...props}
                isAssessor={isAssessor}
                contact={model?.contact}
                registryContact={model?.registryContact}
                onChange={onChange}
                onSpinner={setSpinner}
              />
            }
          />
          <Route path="callsigntransfer" element={<CallsignTransferPage {...props} onSpinner={setSpinner} />} />
          <Route path="*" element={defaultPath ? <Navigate to={defaultPath} /> : noDefaultPathAlert} />
        </Routes>
      </Spinner>

      <AuthorisationModal
        {...props}
        open={authorisation}
        onClose={(validated) => {
          setAuthorisation(false);
          if (validated) {
            const [client] = getApiClient(props.baseUrl, props.b2cDomainName, props.msalClient);
            load(client);
          }
        }}
      />
    </>
  );
};
